/**
 * Line-item fee summary rendered on the event detail page + Razorpay checkout
 * dialog. Uses the /api/checkout/quote server response as the single source
 * of truth so the total shown here matches what Razorpay charges.
 *
 * Props:
 *   quote      — object from GET /api/checkout/quote (or null while loading)
 *   loading    — true while the quote is being fetched
 */
export default function FeeBreakdown({ quote, loading }) {
  if (loading) {
    return (
      <div
        className="border border-zinc-800 bg-[#050505] p-4 text-center"
        data-testid="fee-breakdown-loading"
      >
        <span className="font-mono text-xs text-zinc-500 tracking-widest">CALCULATING…</span>
      </div>
    );
  }
  if (!quote) return null;

  const fmt = (n) =>
    n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const rows = [
    { label: `TICKET × ${quote.quantity}`, value: quote.ticket_subtotal, id: 'ticket_subtotal' },
  ];
  if ((quote.discount_amount || 0) > 0) {
    rows.push({
      label: `DISCOUNT · ${quote.discount_percent}% OFF`,
      value: -quote.discount_amount,
      id: 'discount',
      accent: true,
    });
  }
  if ((quote.platform_fee_total || 0) > 0) {
    rows.push({ label: 'PLATFORM FEE', value: quote.platform_fee_total, id: 'platform_fee' });
  }
  if ((quote.convenience_fee_total || 0) > 0) {
    rows.push({ label: 'CONVENIENCE FEE', value: quote.convenience_fee_total, id: 'convenience_fee' });
  }
  if ((quote.gst_on_fees || 0) > 0) {
    rows.push({ label: 'GST · 18% ON FEES', value: quote.gst_on_fees, id: 'gst_on_fees' });
  }

  return (
    <div
      className="border border-zinc-800 bg-[#050505] divide-y divide-zinc-900"
      data-testid="fee-breakdown"
    >
      {rows.map((r) => (
        <div
          key={r.id}
          className="flex justify-between items-center px-4 py-2.5 text-sm"
          data-testid={`fee-row-${r.id}`}
        >
          <span className={`font-mono text-xs tracking-wider ${r.accent ? 'text-[#B5FF00]' : 'text-zinc-500'}`}>{r.label}</span>
          <span className={`font-mono ${r.accent ? 'text-[#B5FF00]' : 'text-zinc-200'}`}>
            {r.value < 0 ? '-' : ''}₹{fmt(Math.abs(r.value))}
          </span>
        </div>
      ))}
      <div
        className="flex justify-between items-center px-4 py-3 bg-[#B5FF00]/5 border-t border-[#B5FF00]/30"
        data-testid="fee-row-total"
      >
        <span className="font-mono text-xs text-[#B5FF00] tracking-widest font-bold">TOTAL</span>
        <span className="font-['Josefin_Sans'] text-2xl text-[#B5FF00] font-bold">
          ₹{fmt(quote.total)}
        </span>
      </div>
    </div>
  );
}
